import { css } from '@emotion/core'
import { ArrowCaret, ContentAttached } from '@fs/zion-icon'
import { PlaceTemple } from '@fs/zion-icon/dist/cjs/icons'
import { Ellipsis, IconButton, PersonBlock, ResponsiveTableRow, Row, colors, H6 } from '@fs/zion-ui'
import React, { useContext, useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { uniqueId } from 'lodash'
import { DmcResponsiveCheckboxTable } from '../common/DmcResponsiveTable'
import useNotifications from '../../hooks/useNotifications'
import { getTempleInventoryItem } from '../../services/templeInventoryService'
import DmcCopyToClipboardTooltip from '../../util/DmcCopyToClipboardTooltip'
import joinComponents from '../../util/ElementUtil'
import { enumToReadableCamelCase, getEnumAbbreviation } from '../../util/StringUtil'
import { getFrontendBaseUrl, useQueryParams } from '../../util/UrlUtil'
import { GroupLink, PersonLink, UnitLink, UserLink } from '../app/Links'
import { PermissionsContext } from '../app/PermissionsContext'
import { PersonContext } from '../person/PersonContext'
import EditReservationButton from '../person/reservations/EditReservationButton'
import { utcToLocaleString } from '../../util/DateUtil'

const hiddenDetailsLabel = css`
  font-size: 0.9rem;
`
const ranIdLabel = css`
  display: flex;
  justify-content: space-between;
}
`
const ranIdLabelText = css`
  align-self: flex-end;
}
`
const ranIdLabelIcon = css`
  position: relative;
  top: 5px;
}
`
const focusedRanIdStyles = css`
  border-radius: 50px;
  animation: glow 1s 6 alternate;

  @keyframes glow {
    from {
      box-shadow: 0 0 5px -5px ${colors.green03};
    }
    to {
      box-shadow: 0 0 5px 20px ${colors.green03};
      background-color: ${colors.green03};
    }
  }
`

const ShowAllHiddenDetailsButton = ({ showAllHiddenDetails, updateShowAllHiddenDetailsList, t }) => {
  return (
    <span css={ranIdLabel}>
      <span css={ranIdLabelText}>{t('reservations.ranId', 'RAN ID')}</span>
      <span css={ranIdLabelIcon}>
        <IconButton
          Icon={ArrowCaret}
          iconDirection={showAllHiddenDetails ? 'up' : 'down'}
          onClick={() => updateShowAllHiddenDetailsList()}
          data-testid="showAllDetails"
          size="md"
        />
      </span>
    </span>
  )
}

export default function ReservationsTable({ reservations, personData, userNames, setCheckedRanIds }) {
  const { t } = useTranslation()
  const { fetchCurrentPerson } = useContext(PersonContext)
  const [showHiddenDetailsList, setShowHiddenDetailList] = useState([])
  const [showAllHiddenDetails, setShowAddHiddenDetails] = useState(false)
  const queryParams = useQueryParams()
  const ranIdToScrollTo = queryParams.get('ranId')
  const { isAuthorized } = useContext(PermissionsContext)
  const { error } = useNotifications()
  const focusedRanIdRef = useRef()

  useEffect(() => {
    async function mapData() {
      const wardAndStakeMap = new Map()
      const sharedReservations = reservations.filter(
        (res) => res.sharedWithChurch && res.osid && res.onList && !res.printedDate
      )
      const osidSet = new Set(sharedReservations.map((res) => res.osid))
      await Promise.allSettled(
        [...osidSet].map(async (osid) => {
          try {
            const inventoryItem = await getTempleInventoryItem(osid)
            wardAndStakeMap.set(osid, { ward: inventoryItem.ward, stake: inventoryItem.stake })
          } catch (err) {
            error(
              t('person.reservations.errorFetchingWardDetails', `Error retrieving ward/stake information for ${osid}`),
              err
            )
          }
        })
      )
      sharedReservations.forEach((res) => {
        res.ward = wardAndStakeMap.get(res.osid)?.ward
        res.stake = wardAndStakeMap.get(res.osid)?.stake
      })
    }
    mapData()
  }, [error, reservations, t])

  useEffect(() => {
    if (ranIdToScrollTo && focusedRanIdRef.current) {
      focusedRanIdRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' })
      focusedRanIdRef.current.click()
    }
  }, [ranIdToScrollTo])

  const updateShowHiddenDetailsList = (decodedRanId) => {
    const showHiddenDetailsListCopy = JSON.parse(JSON.stringify(showHiddenDetailsList))
    const decodedRanIdIndex = showHiddenDetailsListCopy.findIndex((element) => element === decodedRanId)
    if (decodedRanIdIndex === -1) {
      showHiddenDetailsListCopy.push(decodedRanId)
    } else {
      showHiddenDetailsListCopy.splice(decodedRanIdIndex, 1)
    }
    setShowHiddenDetailList(showHiddenDetailsListCopy)
  }

  const updateShowAllHiddenDetailsList = () => {
    setShowAddHiddenDetails(!showAllHiddenDetails)
    if (!showAllHiddenDetails) {
      setShowHiddenDetailList(reservations.map((res) => res.decodedRanId))
    } else {
      setShowHiddenDetailList([])
    }
  }

  return (
    <DmcResponsiveCheckboxTable
      id="Reservations Table"
      onSelectionChange={setCheckedRanIds}
      totalColumnCount={10}
      separator={<br />}
      atSizeColumns={{
        default: {
          labels: [
            t('person.label', 'Person'),
            t('reservations.ordinance', 'Ord'),
            t('reservations.ownerAssignedTo', 'Owner/Assigned'),
            t('reservations.reservedAssigned', 'Reserved/Assigned'),
            t('reservations.printedExpired', 'Printed/Expires'),
            <ShowAllHiddenDetailsButton
              key={uniqueId('showAllHiddenDetails-')}
              showAllHiddenDetails={showAllHiddenDetails}
              updateShowAllHiddenDetailsList={updateShowAllHiddenDetailsList}
              t={t}
            />,
          ],
          columnWidths: [3, 1, 2, 2, 2, 2],
          combinationRules: [
            ['column1'],
            ['column2'],
            ['column3', 'column4'],
            ['column5', 'column6'],
            ['column7', 'column8', 'column9'],
            ['column10'],
          ],
        },
        sm: {
          labels: [
            t('person.label', 'Person'),
            t('reservations.ordinanceOwnerAssigned', 'Ordinance/ Owner/ Assigned'),
            t('reservations.reservedAssignedPrintedExpires', 'Reserved/ Assigned/ Printed/ Expires'),
            <ShowAllHiddenDetailsButton
              key={uniqueId('showAllHiddenDetails-')}
              showAllHiddenDetails={showAllHiddenDetails}
              updateShowAllHiddenDetailsList={updateShowAllHiddenDetailsList}
              t={t}
            />,
          ],
          columnWidths: [3, 3, 4, 2],
          combinationRules: [
            ['column1'],
            ['column2', 'column3', 'column4'],
            ['column5', 'column6', 'column7', 'column8'],
            ['column10', 'column9'],
          ],
        },
        xl: {
          labels: [
            t('person.label', 'Person'),
            t('reservations.ordinance', 'Ord'),
            t('reservations.ownerAssignedTo', 'Owner/Assigned'),
            t('reservations.reservedAssigned', 'Reserved/Assigned'),
            t('reservations.printedExpired', 'Printed/Expires'),
            <ShowAllHiddenDetailsButton
              key={uniqueId('showAllHiddenDetails-')}
              showAllHiddenDetails={showAllHiddenDetails}
              updateShowAllHiddenDetailsList={updateShowAllHiddenDetailsList}
              t={t}
            />,
          ],
          columnWidths: [3, 1, 2, 2, 2, 2],
          combinationRules: [
            ['column1'],
            ['column2'],
            ['column3', 'column4'],
            ['column5', 'column6'],
            ['column7', 'column8', 'column9'],
            ['column10'],
          ],
        },
      }}
    >
      {reservations.map((res) => {
        const shouldFocusRanId = ranIdToScrollTo === res.ranId
        const showHiddenDetails = showHiddenDetailsList.includes(res.decodedRanId)
        return (
          <ResponsiveTableRow
            key={res.decodedRanId}
            name={res.decodedRanId}
            column1={
              <PersonReservationPersons
                reservation={res}
                showHiddenDetails={showHiddenDetails}
                personData={personData}
              />
            }
            column2={<Ellipsis>{getEnumAbbreviation(res.ordinanceType)}</Ellipsis>}
            column3={
              <Ellipsis>
                <UserLink showId userId={res.ownerId} userName={userNames && userNames[res.ownerId]} />
              </Ellipsis>
            }
            column4={
              <>
                {res.familyGroupIds?.length > 0 ? (
                  <Row>
                    <Ellipsis>
                      {t(`reservation.assignedTo.GROUP`, 'Group')}
                      <span>
                        {' ('}
                        {joinComponents(
                          res.familyGroupIds.map((groupId) => <GroupLink groupId={groupId} />),
                          <br />
                        )}
                        )
                      </span>
                    </Ellipsis>
                  </Row>
                ) : (
                  <Ellipsis>
                    {t(
                      `reservation.assignedTo.${res.sharedWithChurch ? 'CHURCH' : 'INDIVIDUAL'}`,
                      res.sharedWithChurch ? 'CHURCH' : 'INDIVIDUAL'
                    )}
                  </Ellipsis>
                )}
                {showHiddenDetails && (
                  <>
                    <br />
                    <strong css={hiddenDetailsLabel}>{`${t('reservations.onUserList', 'On User List')}: `}</strong>
                    {res.onList ? 'Yes' : 'No'}
                    <br />
                  </>
                )}
              </>
            }
            column5={<Ellipsis>{utcToLocaleString(res.reservedDate, true)}</Ellipsis>}
            column6={
              <>
                <Ellipsis>{utcToLocaleString(res.assignedDate, true)}</Ellipsis>
                {showHiddenDetails && (
                  <>
                    <br />
                    <strong css={hiddenDetailsLabel}>{`${t('lastModified', 'Last Modified')}: `}</strong>
                    {res.lastModifiedDate}
                  </>
                )}
              </>
            }
            column7={
              <Ellipsis>
                {res.printedDate
                  ? utcToLocaleString(res.printedDate, true)
                  : t('reservations.notPrinted', 'Not Printed')}
              </Ellipsis>
            }
            column8={
              <>
                <Ellipsis>
                  {res.expiryDate ? (
                    <span
                      style={{
                        color:
                          res.expiryType === 'Shared Ready' ||
                          res.expiryType === 'Family Group' ||
                          res.expiryType === 'Instant Names'
                            ? colors.red60
                            : '',
                      }}
                    >
                      {utcToLocaleString(res.expiryDate, true)}
                    </span>
                  ) : (
                    t('reservations.noExpiryDate', 'No Exp Date')
                  )}
                </Ellipsis>
                {showHiddenDetails && res.reservationType != null && (
                  <>
                    <br />
                    <strong css={hiddenDetailsLabel}>{`${t('res.expiryType', 'Expiry Type')}: `}</strong>
                    {t(`reservation.expiryType.${res.reservationType}`, enumToReadableCamelCase(res.reservationType))}
                  </>
                )}
              </>
            }
            column9={
              <>
                {showHiddenDetails && res.stake && (
                  <>
                    <strong css={hiddenDetailsLabel}>{`${t('reservations.stake', 'Stake')}: `}</strong>
                    <UnitLink unitnumber={res.stake} />
                    <br />
                  </>
                )}
                {showHiddenDetails && res.ward && (
                  <>
                    <strong css={hiddenDetailsLabel}>{`${t('reservations.ward', 'Ward')}: `}</strong>
                    <UnitLink unitnumber={res.ward} />
                  </>
                )}
              </>
            }
            column10={
              <>
                <Row alignY="middle" wrap gutters="xs">
                  <DmcCopyToClipboardTooltip
                    value={res.ranId}
                    body={shouldFocusRanId && <span css={focusedRanIdStyles}>{res.ranId}</span>}
                  />
                  <EditReservationButton reservation={res} onSuccess={fetchCurrentPerson} />
                  <IconButton
                    Icon={ArrowCaret}
                    iconDirection={showHiddenDetails ? 'up' : 'down'}
                    onClick={() => updateShowHiddenDetailsList(res.decodedRanId)}
                    size="xs"
                    ref={shouldFocusRanId ? focusedRanIdRef : undefined}
                  />
                  {res.osid && (
                    <IconButton
                      Icon={ContentAttached}
                      onClick={() =>
                        window.open(
                          `${getFrontendBaseUrl()}/temple/ows/${res.osid}/summary?idType=osid`,
                          '_blank',
                          'noopener, noreferrer'
                        )
                      }
                      description={res.osid}
                      size="xs"
                    />
                  )}
                  {res.pinId && (
                    <IconButton
                      Icon={PlaceTemple}
                      onClick={() =>
                        window.open(
                          `${getFrontendBaseUrl()}/temple/inventory-info/${res.pinId}/summary`,
                          '_blank',
                          'noopener, noreferrer'
                        )
                      }
                      size="xs"
                    />
                  )}
                </Row>
                {res.decodedRanId && (
                  <Row>
                    <span>
                      (
                      <DmcCopyToClipboardTooltip value={res.decodedRanId} />)
                    </span>
                  </Row>
                )}
              </>
            }
            disabled={!isAuthorized('ReserveOrdinancePermission')}
          />
        )
      })}
    </DmcResponsiveCheckboxTable>
  )
}

function ReservationPerson({ showHiddenDetails, gender, name, personId, originalId }) {
  let details = <PersonLink personId={personId} />
  if (showHiddenDetails) {
    details = (
      <>
        Current: <PersonLink personId={personId} />
        <br />
        Original: <PersonLink personId={originalId} />
      </>
    )
  }
  return (
    <div>
      <PersonBlock
        size="sm"
        avatarProps={{
          sex: gender.toLowerCase(),
        }}
        name={name}
        details={details}
      />
    </div>
  )
}

function PersonReservationPersons({ reservation, showHiddenDetails, personData }) {
  const { t } = useTranslation()
  const personCurrent = personData[reservation.currentIndividualId]
  const personOriginal = personData[reservation.originalIndividualId]
  const childCurrent = personData[reservation.currentIndividualId]
  const childOriginal = personData[reservation.originalIndividualId]
  const parent1Current = personData[reservation.currentSpouse1Id]
  const parent2Current = personData[reservation.currentSpouse2Id]
  const parent1Original = personData[reservation.originalSpouse1Id]
  const parent2Original = personData[reservation.originalSpouse2Id]
  const spouse1Current = personData[reservation.currentSpouse1Id]
  const spouse2Current = personData[reservation.currentSpouse2Id]
  const spouse1Original = personData[reservation.originalSpouse1Id]
  const spouse2Original = personData[reservation.originalSpouse2Id]
  switch (reservation.ordinanceType) {
    case 'SEALING_TO_SPOUSE':
      return (
        <>
          <ReservationPerson
            showHiddenDetails={showHiddenDetails}
            gender={spouse1Current.gender}
            name={spouse1Current.name}
            personId={spouse1Current.personId}
            originalId={spouse1Original.personId}
          />
          <H6>{t('spouse', 'Spouse')}</H6>
          <ReservationPerson
            showHiddenDetails={showHiddenDetails}
            gender={spouse2Current.gender}
            name={spouse2Current.name}
            personId={spouse2Original.personId}
            originalId={spouse2Original.personId}
          />
        </>
      )
    case 'SEALING_TO_PARENTS':
      return (
        <>
          <ReservationPerson
            showHiddenDetails={showHiddenDetails}
            gender={childCurrent.gender}
            name={childCurrent.name}
            personId={childCurrent.personId}
            originalId={childOriginal.personId}
          />
          <H6>{t('parents', 'Parents')}</H6>
          <ReservationPerson
            showHiddenDetails={showHiddenDetails}
            gender={parent1Current.gender}
            name={parent1Current.name}
            personId={parent1Current.personId}
            originalId={parent1Original.personId}
          />
          <ReservationPerson
            showHiddenDetails={showHiddenDetails}
            gender={parent2Current.gender}
            name={parent2Current.name}
            personId={parent2Current.personId}
            originalId={parent2Original.personId}
          />
        </>
      )
    default: {
      return (
        <ReservationPerson
          showHiddenDetails={showHiddenDetails}
          gender={personCurrent.gender}
          name={personCurrent.name}
          personId={personCurrent.personId}
          originalId={personOriginal.personId}
        />
      )
    }
  }
}
